// Iter185 — Position Management: scannable table layout + Final Assessment.
// Iter186a — "Rules Status" header (neutral framing), Details drawer removed,
// Valuation cell restructured into Multiples / YoY Trend / Balance Sheet
// sub-blocks (Intrinio-primary, yfinance-fallback, per-field source badges).
// Rules Status: system-computed (transparent rule matrix) shown as a
// pre-filled dropdown the user can override; both values persist separately.
import React from "react";
import api from "@/lib/api";
import { RiskHealthPanel } from "@/components/RiskHealthLED";
import { PortfolioOptimizationModal } from "@/components/PortfolioOptimizationModal";

const SEVERITY_STYLE = {
  0: { bg: "rgba(16,185,129,0.10)", fg: "#10B981" },
  1: { bg: "rgba(59,130,246,0.12)", fg: "#60A5FA" },
  2: { bg: "rgba(245,158,11,0.14)", fg: "#F59E0B" },
  3: { bg: "rgba(239,68,68,0.14)", fg: "#F87171" },
};
const SIGNAL_STYLE = {
  HOLD: { bg: "rgba(113,113,122,0.15)", fg: "#A1A1AA" },
  ROLL: { bg: "rgba(245,158,11,0.14)", fg: "#F59E0B" },
  CLOSE: { bg: "rgba(239,68,68,0.14)", fg: "#F87171" },
};
const ACTION_OPTIONS = [
  { value: "HOLD", label: "Hold" },
  { value: "TRIM", label: "Trim" },
  { value: "EXIT", label: "Exit" },
  { value: "HEDGE", label: "Hedge" },
  { value: "ADD", label: "Add" },
  { value: "UNDER_REVIEW", label: "Under Review" },
];
const ACTION_COLOR = {
  HOLD: "#A1A1AA", TRIM: "#F59E0B", EXIT: "#F87171",
  HEDGE: "#60A5FA", ADD: "#10B981", UNDER_REVIEW: "#C084FC",
};

const td = "px-1.5 py-1 text-[10px] align-top leading-tight whitespace-nowrap";
const tdWrap = "px-1.5 py-1 text-[10px] align-top leading-tight";
const th = "px-1.5 py-1 text-[9px] font-bold uppercase tracking-wide text-left";

function Src({ s }) {
  return null;
}

// Iter186a layout pass — Multiples / YoY Trend / Balance Sheet are separate
// table columns; metrics inline "label: value | …" with I/Y source badges;
// FCF shown as nominal $ change (percentage misleads on small/negative bases).
function fmtUsdDelta(x) {
  const a = Math.abs(x);
  const s = a >= 1e9 ? `${(a / 1e9).toFixed(1)}B` : a >= 1e6 ? `${Math.round(a / 1e6)}M`
    : a >= 1e3 ? `${Math.round(a / 1e3)}K` : `${Math.round(a)}`;
  return `${x >= 0 ? "+" : "-"}$${s}`;
}

function InlineCell({ segs, rows, testid, title, perLine = 2 }) {
  let lines;
  if (rows) {
    lines = rows.map(r => r.filter(Boolean)).filter(l => l.length);
  } else {
    const items = segs.filter(Boolean);
    lines = [];
    for (let i = 0; i < items.length; i += perLine) lines.push(items.slice(i, i + perLine));
  }
  if (!lines.length) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  return (
    <div className="font-mono-data leading-tight" data-testid={testid} title={title}
      style={{ color: "var(--hoip-text-secondary)" }}>
      {lines.map((line, li) => (
        <div key={li} className="whitespace-nowrap">
          {line.map((s, i) => (
            <span key={i}>
              {s}
              {i < line.length - 1 && <span style={{ color: "var(--hoip-text-tertiary)", opacity: 0.5 }}>{" | "}</span>}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function MultiplesCell({ v, ticker }) {
  if (!v) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  const src = (f) => (v.sources || {})[f];
  const m = (label, val, f) => val != null ? (
    <span key={f}><span style={{ color: "var(--hoip-text-tertiary)" }}>{label}: </span>{val}<Src s={src(f)} /></span>
  ) : null;
  return <InlineCell testid={`pm-val-multiples-${ticker}`} rows={[
    [m("P/E", v.pe_ttm, "pe_ttm"), m("Fwd P/E", v.pe_forward, "pe_forward")],
    [m("EV/EBITDA", v.ev_ebitda, "ev_ebitda"), m("PEG", v.peg, "peg")],
  ]} />;
}

function TrendCell({ v, ticker }) {
  const t = v?.fundamentals_trend;
  if (!t) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  const src = (f) => (v.sources || {})[f];
  const dir = (up) => ({ color: up ? "#10B981" : "#F87171" });
  const pctSeg = (label, m, f) => m?.yoy_pct != null ? (
    <span key={f}><span style={{ color: "var(--hoip-text-tertiary)" }}>{label}: </span>
      <span style={dir(m.yoy_pct >= 0)}>{m.yoy_pct >= 0 ? "+" : ""}{m.yoy_pct}%</span><Src s={src(f)} /></span>
  ) : null;
  const fcfSeg = () => {
    const m = t.fcf;
    if (!m || m.latest == null || m.prior == null) return null;
    const d = m.latest - m.prior;
    return (
      <span key="fcf" title={`Free cash flow ${d >= 0 ? "increased" : "decreased"} vs same quarter prior year (prior: ${fmtUsdDelta(m.prior).replace("+", "")}, latest: ${fmtUsdDelta(m.latest).replace("+", "")})`}>
        <span style={{ color: "var(--hoip-text-tertiary)" }}>FCF: </span>
        <span style={dir(d >= 0)}>{fmtUsdDelta(d)}</span><Src s={src("fcf")} />
      </span>
    );
  };
  const marginSeg = () => {
    const opm = t.operating_margin;
    if (opm?.delta_pp != null) return (
      <span key="opm" title={`Operating margin ${opm.delta_pp >= 0 ? "expanded" : "contracted"}: ${opm.prior_pct}% → ${opm.latest_pct}%`}>
        <span style={{ color: "var(--hoip-text-tertiary)" }}>Op Mgn: </span>
        <span style={dir(opm.delta_pp >= 0)}>{opm.delta_pp >= 0 ? "+" : ""}{opm.delta_pp}pp</span><Src s={src("operating_margin")} />
      </span>
    );
    const er = t.efficiency_ratio;
    if (er?.delta_pp != null) return (
      // Bank efficiency ratio — lower is better, so falling = green.
      <span key="er" title={`Bank efficiency ratio (noninterest expense ÷ revenue — lower is better): ${er.prior_pct}% → ${er.latest_pct}%. Operating margin is not reported for financials.`}>
        <span style={{ color: "var(--hoip-text-tertiary)" }}>Eff Ratio: </span>
        <span style={dir(er.delta_pp <= 0)}>{er.delta_pp >= 0 ? "+" : ""}{er.delta_pp}pp</span><Src s={src("efficiency_ratio")} />
      </span>
    );
    return null;
  };
  return <InlineCell testid={`pm-val-trend-${ticker}`}
    title={t.period_label ? `Latest reported quarter vs same quarter prior year (${t.period_label})` : undefined}
    segs={[fcfSeg(), marginSeg(), pctSeg("Rev", t.revenue, "revenue"),
      pctSeg("EPS", t.diluted_eps, "diluted_eps"), pctSeg("Net Inc", t.net_income, "net_income")]} />;
}

function BalanceCell({ v, ticker }) {
  const bs = v?.balance_sheet;
  if (!bs) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  const src = (f) => (v.sources || {})[f];
  return <InlineCell testid={`pm-val-balance-${ticker}`} perLine={1} segs={[
    bs.debt_to_equity != null && (
      <span key="de"><span style={{ color: "var(--hoip-text-tertiary)" }}>D/E: </span>{bs.debt_to_equity}<Src s={src("debt_to_equity")} /></span>
    ),
    bs.interest_coverage != null && (
      <span key="ic" title="Interest Coverage — EBIT relative to interest expense (latest reported quarter)">
        <span style={{ color: "var(--hoip-text-tertiary)" }}>Int Cov: </span>{bs.interest_coverage}×<Src s={src("interest_coverage")} />
      </span>
    ),
  ]} />;
}

function AnalystCell({ v }) {
  const a = v?.analyst;
  if (!a || (!a.rating && a.target_mean == null)) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  return (
    <div className="font-mono-data leading-tight" style={{ color: "var(--hoip-text-secondary)" }}>
      <div className="whitespace-nowrap">
        {a.rating || "—"}
        {a.target_mean != null && <> · ${a.target_mean}</>}
        <Src s={(v.sources || {}).analyst} />
      </div>
      {(a.upside_pct != null || a.analyst_count != null) && (
        <div className="whitespace-nowrap">
          {a.upside_pct != null && (
            <span style={{ color: a.upside_pct >= 0 ? "#10B981" : "#F87171" }}>{a.upside_pct >= 0 ? "+" : ""}{a.upside_pct}%</span>
          )}
          {a.analyst_count != null && <span style={{ color: "var(--hoip-text-tertiary)" }}>{a.upside_pct != null ? " · " : ""}{a.analyst_count}a</span>}
        </div>
      )}
    </div>
  );
}

function AssessmentCell({ r, onSelect }) {
  const a = r.assessment;
  if (!a) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  const current = a.user_selected || a.suggested;
  const overridden = !!a.user_selected;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <select
          value={current}
          onChange={(e) => onSelect(r.ticker, e.target.value)}
          className="h-5 text-[10px] font-bold rounded-sm border px-1 cursor-pointer"
          style={{ background: "var(--hoip-nav)", borderColor: (ACTION_COLOR[current] || "#71717A") + "66", color: ACTION_COLOR[current] || "#A1A1AA" }}
          data-testid={`pm-assessment-select-${r.ticker}`}>
          {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {overridden ? (
          <button onClick={() => onSelect(r.ticker, null)}
            className="text-[8px] px-1 rounded-sm"
            style={{ background: "rgba(113,113,122,0.15)", color: "#A1A1AA" }}
            title={`Your override. System suggested: ${a.suggested}. Click to reset.`}
            data-testid={`pm-assessment-yours-${r.ticker}`}>
            yours · sys: {a.suggested.replace("_", " ")}
          </button>
        ) : (
          <span className="text-[8px] px-1 rounded-sm"
            style={{ background: "rgba(113,113,122,0.15)", color: "#A1A1AA" }}
            title={a.rationale}
            data-testid={`pm-assessment-suggested-${r.ticker}`}>
            suggested{a.partial_data ? " · partial data" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

function StocksTable({ stocks, onSelect }) {
  return (
    <div className="overflow-x-auto">
      {/* No w-full: the table shrink-wraps to content so auto layout can't
          distribute surplus width into gutters between columns. */}
      <table style={{ width: "max-content" }} data-testid="pm-stocks-table">
        <thead>
          <tr className="border-b" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
            <th className={th}>Ticker</th>
            <th className={th}>Price · Weight</th>
            <th className={th}>Technical</th>
            <th className={th}>Regime</th>
            <th className={th} title="Concentration — position size as % of Net Liquidation Value">Conc %</th>
            <th className={th}>Multiples</th>
            <th className={th} title="Latest reported quarter vs same quarter one year prior">YoY Trend</th>
            <th className={th}>Balance Sheet</th>
            <th className={th}>Analyst</th>
            <th className={th}>Rules</th>
            <th className={th}>
              Rules Status
              <span className="block normal-case font-normal text-[7px] whitespace-normal max-w-[130px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                Informational starting point — your decision is yours
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {stocks.map((r) => {
            const ss = SEVERITY_STYLE[r.severity] || SEVERITY_STYLE[0];
            const hasBreach = (r.breaches || []).length > 0;
            const belowSma = r.sma != null && r.price < r.sma;
            const concBreach = (r.breaches || []).some(b => b.id === "single_position");
            return (
              <tr key={r.ticker} className="border-b hover:bg-[var(--hoip-hover)] transition-colors"
                style={{ borderColor: "var(--hoip-divider)", background: hasBreach ? "rgba(239,68,68,0.03)" : "transparent" }}
                data-testid={`pm-stock-row-${r.ticker}`}>
                <td className={`${td} font-bold text-xs`} style={{ color: "var(--hoip-text-primary)" }}>{r.ticker}</td>
                <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-secondary)" }}>
                  ${r.price}
                  <span className="block" style={{ color: (r.pnl_pct || 0) >= 0 ? "#10B981" : "#F87171" }}>
                    {r.pnl_pct != null ? `${r.pnl_pct > 0 ? "+" : ""}${r.pnl_pct}%` : "—"}
                    <span style={{ color: "var(--hoip-text-tertiary)" }}> · {r.nlv_pct != null ? `${r.nlv_pct}% NLV` : "—"}</span>
                  </span>
                </td>
                <td className={`${td} font-mono-data`} style={{ color: r.sma == null ? "var(--hoip-text-tertiary)" : belowSma ? "#F87171" : "#10B981" }}
                  data-testid={`pm-technical-${r.ticker}`}>
                  {r.sma != null ? (
                    <>
                      <span className="block">{belowSma ? "▼ Below" : "▲ Above"} {r.sma_period}SMA</span>
                      <span className="block">${r.sma}</span>
                    </>
                  ) : "—"}
                </td>
                <td className={td} style={{ color: /volatil|bear/i.test(r.regime_label || "") ? "#F59E0B" : "var(--hoip-text-tertiary)" }}>
                  {r.regime_label || "—"}
                </td>
                <td className={`${td} font-mono-data`} style={{ color: concBreach ? "#F87171" : "var(--hoip-text-tertiary)" }}
                  data-testid={`pm-concentration-${r.ticker}`}>
                  {r.nlv_pct != null ? `${r.nlv_pct}%${concBreach ? " ⚠" : ""}` : "—"}
                </td>
                <td className={tdWrap} style={{ minWidth: 150, maxWidth: 210 }} data-testid={`pm-valuation-${r.ticker}`}><MultiplesCell v={r.valuation} ticker={r.ticker} /></td>
                <td className={tdWrap} style={{ minWidth: 170, maxWidth: 250 }}><TrendCell v={r.valuation} ticker={r.ticker} /></td>
                <td className={tdWrap} style={{ minWidth: 110, maxWidth: 160 }}><BalanceCell v={r.valuation} ticker={r.ticker} /></td>
                <td className={td} data-testid={`pm-analyst-${r.ticker}`}><AnalystCell v={r.valuation} /></td>
                <td className={td}>
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-sm border whitespace-nowrap"
                    style={{ background: ss.bg, color: ss.fg, borderColor: ss.fg + "44" }}
                    title={(r.reasons || []).map(x => x.text).join("\n") || "No configured rules triggered"}
                    data-testid={`pm-status-${r.ticker}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: ss.fg }} />
                    {r.rules_triggered ?? 0}{hasBreach ? " ⚠" : ""}
                  </span>
                  {r.cc_context?.eligible && (
                    <span className="inline-flex items-center gap-1 ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-sm border whitespace-nowrap cursor-help"
                      style={{ background: "rgba(16,185,129,0.10)", color: "#10B981", borderColor: "#10B98144" }}
                      title={(r.cc_context.conditions || []).join("\n") + "\n\n" + (r.cc_context.note || "")}
                      data-testid={`pm-cc-badge-${r.ticker}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981" }} />
                      CC
                    </span>
                  )}
                  {r.profit_protection && (
                    <span className="block mt-0.5 text-[8px] font-bold px-1 py-0.5 rounded-sm whitespace-nowrap cursor-help"
                      style={r.profit_protection.mode === "protective_put_context"
                        ? { background: "rgba(59,130,246,0.12)", color: "#3B82F6" }
                        : r.profit_protection.mode === "covered_call_context"
                          ? { background: "rgba(16,185,129,0.12)", color: "#10B981" }
                          : { background: "rgba(245,158,11,0.12)", color: "#F59E0B" }}
                      title={r.profit_protection.note + (r.profit_protection.estimate
                        ? `\n\nNearest protective put: $${r.profit_protection.estimate.strike} exp ${r.profit_protection.estimate.expiry} (~${r.profit_protection.estimate.dte}d) ≈ $${r.profit_protection.estimate.cost_per_contract}/contract`
                        : "")}
                      data-testid={`pm-profit-protect-${r.ticker}`}>
                      {r.profit_protection.mode === "protective_put_context" ? "PROFIT-LOCK · PUT CONTEXT"
                        : r.profit_protection.mode === "covered_call_context" ? "PROFIT-LOCK · CALL CONTEXT"
                        : "PROFIT-LOCK · REVIEW"}
                    </span>
                  )}
                </td>
                <td className={td}><AssessmentCell r={r} onSelect={onSelect} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IncomeRow({ r }) {
  const [expanded, setExpanded] = React.useState(false);
  const adv = r.advisor || {};
  const ss = SIGNAL_STYLE[adv.signal] || SIGNAL_STYLE.HOLD;
  const strike = r.short_strike != null ? r.short_strike : r.long_strike;
  return (
    <div className="border-b px-3 py-1 flex items-center gap-3 flex-wrap"
      style={{ borderColor: "var(--hoip-divider)" }}
      data-testid={`pm-income-row-${r.ticker}-${r.trade_id || strike}`}>
      <span className="text-xs font-bold w-14" style={{ color: "var(--hoip-text-primary)" }}>{r.ticker}</span>
      <span className="text-[9px] uppercase w-28 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>
        {(r.strategy || "").replace(/_/g, " ")} {strike != null ? `$${strike}` : ""}
      </span>
      <span className="text-[10px] font-mono-data w-14"
        style={{ color: adv.gamma_window ? "#F87171" : "var(--hoip-text-secondary)" }}
        title={adv.gamma_window ? "Inside the gamma-risk window (≤ your DTE threshold)" : "Days to expiration"}
        data-testid={`pm-dte-${r.ticker}`}>
        {r.dte != null ? `${r.dte}d` : "—"}
      </span>
      <span className="text-[10px] font-mono-data w-12" style={{ color: "var(--hoip-text-secondary)" }} title="Short-strike delta">
        {r.delta != null ? `${Math.round(Math.abs(r.delta) * 100)}Δ` : "—"}
      </span>
      <span className="text-[10px] font-mono-data w-16"
        style={{ color: adv.early_warning ? "#F59E0B" : "var(--hoip-text-secondary)" }}
        title="Probability of touch ≈ 2 × |delta|" data-testid={`pm-pot-${r.ticker}`}>
        POT {adv.pot_pct != null ? `${adv.pot_pct}%` : "—"}
      </span>
      <span className="text-[10px] font-mono-data w-16"
        style={{ color: (r.pnl_pct || 0) >= 0 ? "#10B981" : "#F87171" }}>
        {r.pnl_pct != null ? `${r.pnl_pct > 0 ? "+" : ""}${r.pnl_pct}%` : "—"}
      </span>
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap"
        style={{ background: ss.bg, color: ss.fg }} data-testid={`pm-signal-${r.ticker}`} title={adv.reason}>
        {adv.signal || "HOLD"}
      </span>
      {adv.early_warning && (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm"
          style={{ background: "rgba(245,158,11,0.14)", color: "#F59E0B" }}
          data-testid={`pm-early-warning-${r.ticker}`} title={adv.reason}>EARLY WARNING</span>
      )}
      {adv.gamma_window && (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm"
          style={{ background: "rgba(239,68,68,0.12)", color: "#F87171" }}
          data-testid={`pm-gamma-window-${r.ticker}`}
          title="Final weeks before expiration — highest gamma-risk phase; your rules call for an active decision.">GAMMA WINDOW</span>
      )}
      <span className="text-[9px] flex-1 min-w-[180px]" style={{ color: "var(--hoip-text-tertiary)" }}>{adv.reason}</span>
      {/* Iter203 — compact chips on the primary line; full fundamentals /
          valuation detail collapsed behind a per-row toggle. */}
      {r.fundamentals_score != null && (
        <span className="text-[9px] font-mono-data cursor-help whitespace-nowrap"
          style={{ color: r.fundamentals_score >= 60 ? "#10B981" : r.fundamentals_score >= 40 ? "#F59E0B" : "#F87171" }}
          title="Transparent fundamentals composite (0-100): average of the available sub-scores — revenue YoY, EPS YoY, operating-margin change, debt/equity, interest coverage, analyst upside. Expand the row for the full breakdown."
          data-testid={`pm-income-fscore-${r.ticker}`}>
          Fund {r.fundamentals_score}
        </span>
      )}
      {r.suggested_path && (() => {
        const m = r.suggested_path.mode;
        const cfg = m === "close" ? { l: "CLOSE", bg: "rgba(239,68,68,0.12)", fg: "#F87171" }
          : m === "let_assign" ? { l: "LET ASSIGN → CC", bg: "rgba(16,185,129,0.12)", fg: "#10B981" }
          : m === "roll" ? { l: "ROLL", bg: "rgba(245,158,11,0.12)", fg: "#F59E0B" }
          : { l: "HOLD", bg: "rgba(113,113,122,0.12)", fg: "#A1A1AA" };
        return (
          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm cursor-help whitespace-nowrap"
            style={{ background: cfg.bg, color: cfg.fg }}
            title={r.suggested_path.note + " Informational only, not a trade instruction — the decision you record is your own."}
            data-testid={`pm-suggested-path-${r.ticker}`}>
            {cfg.l} <span style={{ fontWeight: 400, opacity: 0.7 }}>· suggested</span>
          </span>
        );
      })()}
      <button onClick={() => setExpanded(v => !v)}
        className="text-[9px] px-1.5 py-0.5 rounded-sm border transition-colors hover:bg-[#1A1A1A]"
        style={{ borderColor: "var(--hoip-border)", color: expanded ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
        title={expanded ? "Hide fundamentals detail" : "Show fundamentals / valuation / analyst detail"}
        data-testid={`pm-income-expand-${r.ticker}-${r.trade_id || strike}`}>
        {expanded ? "▾ Detail" : "▸ Detail"}
      </button>
      {expanded && (
        <div className="w-full flex items-start gap-4 flex-wrap pl-14 py-1 text-[9px] border-t"
          style={{ borderColor: "var(--hoip-divider)" }}
          data-testid={`pm-income-context-${r.ticker}`}>
          {r.underlying_price != null && (
            <span className="font-mono-data" style={{ color: "var(--hoip-text-primary)" }}
              title="Current underlying price">
              ${r.underlying_price.toFixed ? r.underlying_price.toFixed(2) : r.underlying_price}
            </span>
          )}
          {r.sma != null && r.underlying_price != null && (() => {
            const above = r.underlying_price >= r.sma;
            return (
              <span className="font-mono-data" style={{ color: above ? "#10B981" : "#F87171" }}
                title={`Underlying vs its ${r.sma_period}-day SMA — same technical check as the equities table`}
                data-testid={`pm-income-technical-${r.ticker}`}>
                {above ? "▲ Above" : "▼ Below"} {r.sma_period}SMA ${r.sma}
              </span>
            );
          })()}
          {r.regime_label && (
            <span style={{
              color: { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" }[r.regime_label] || "var(--hoip-text-secondary)",
            }} data-testid={`pm-income-regime-${r.ticker}`}>{r.regime_label}</span>
          )}
          {r.nlv_pct != null && (
            <span className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
              title={`Strike notional $${(r.notional || 0).toLocaleString()} as % of Net Liquidation Value — the capital committed if a short put is assigned (cash-secured convention)`}
              data-testid={`pm-income-nlv-${r.ticker}`}>
              {r.nlv_pct}% NLV
            </span>
          )}
          {r.iv_rank != null && (
            <span className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
              title={r.iv_rank_source === "iv-history"
                ? "IV Rank from stored daily ATM implied-vol history (52-week percentile)."
                : "Approximate IV Rank vs a 52-week realized-vol band. This is a proxy, not broker-style IV Rank."}>
              {r.iv_rank_source === "iv-history" ? "" : "~"}IVR {r.iv_rank}</span>
          )}
          <span style={{ minWidth: 140 }}><MultiplesCell v={r.valuation} ticker={`inc-${r.ticker}`} /></span>
          <span style={{ minWidth: 160 }}><TrendCell v={r.valuation} ticker={`inc-${r.ticker}`} /></span>
          <span style={{ minWidth: 100 }}><BalanceCell v={r.valuation} ticker={`inc-${r.ticker}`} /></span>
          <span><AnalystCell v={r.valuation} /></span>
          {r.suggested_path && r.suggested_path.mode !== "hold" && (
            <span className="text-[9px] max-w-[560px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              {r.suggested_path.note}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Covered Call Candidates — held tickers without an open CC, same selection
// engine as the Wheel Income screener's CC intent (Strike/Δ/DTE/Premium/Yield-wk).
function CCCandidatesSection() {
  const [cc, setCc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    api.get("/position-management/cc-candidates")
      .then(({ data }) => { if (mounted) setCc(data); })
      .catch(() => { if (mounted) setError(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const entries = cc?.entries || [];
  const excluded = cc?.excluded_open_cc || [];
  return (
    <section className="border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid="pm-cc-candidates-section">
      <div className="px-3 py-2 border-b flex items-center justify-between flex-wrap gap-1" style={{ borderColor: "var(--hoip-divider)" }}>
        <span className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
          Covered Call Candidates{!loading && <span className="font-normal" style={{ color: "var(--hoip-text-tertiary)" }}> ({entries.length})</span>}
        </span>
        <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          Held tickers without an open covered call · same selection engine as the Wheel Income CC screen · informational only
          {excluded.length > 0 && (
            <span className="ml-1 cursor-help" title={`Excluded — covered call already open: ${excluded.join(", ")}`}
              data-testid="pm-cc-excluded-count">
              · {excluded.length} excluded (CC open)
            </span>
          )}
        </span>
      </div>
      {loading && (
        <div className="px-3 py-4 text-center text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pm-cc-loading">
          Scanning chains for held tickers…
        </div>
      )}
      {error && <div className="px-3 py-4 text-center text-[10px] text-red-400" data-testid="pm-cc-error">Failed to load covered-call candidates.</div>}
      {!loading && !error && entries.length === 0 && (
        <div className="px-3 py-4 text-center text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pm-cc-empty">
          No candidates — requires ≥100 shares of a held ticker with no covered call already open.
        </div>
      )}
      {!loading && !error && entries.length > 0 && (
        <div className="overflow-x-auto">
          <table style={{ width: "max-content", minWidth: "60%" }} data-testid="pm-cc-table">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
                <th className={th}>Ticker</th>
                <th className={th}>Strike</th>
                <th className={th} title="Short-call delta from the chain">Δ</th>
                <th className={th}>DTE</th>
                <th className={th} title="Premium per contract (mid)">Premium</th>
                <th className={th} title="Weekly return-on-risk — premium / covered stock capital, per week">Yield/wk</th>
                <th className={th}>Cts</th>
                <th className={th}>Regime</th>
                <th className={th}>IV Rank</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.ticker} className="border-b hover:bg-[var(--hoip-hover)] transition-colors"
                  style={{ borderColor: "var(--hoip-divider)" }} data-testid={`pm-cc-row-${e.ticker}`}>
                  <td className={`${td} font-bold text-xs`} style={{ color: "var(--hoip-text-primary)" }} title={e.note}>{e.ticker}</td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-secondary)" }}
                    data-testid={`pm-cc-strike-${e.ticker}`}>
                    ${e.short_strike}{e.expiry ? <span style={{ color: "var(--hoip-text-tertiary)" }}> · {e.expiry}</span> : null}
                  </td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-secondary)" }}>
                    {e.delta != null ? Math.abs(e.delta).toFixed(2) : "—"}
                  </td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-secondary)" }}>{e.dte != null ? `${e.dte}d` : "—"}</td>
                  <td className={`${td} font-mono-data`} style={{ color: "#10B981" }}
                    title={e.contracts > 1 ? `$${e.premium_per} × ${e.contracts} contracts = $${Math.round((e.premium_per || 0) * e.contracts)} total` : undefined}>
                    ${e.premium_per}
                  </td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-secondary)" }}
                    data-testid={`pm-cc-ypw-${e.ticker}`}>
                    {e.yield_pct_per_week != null ? `${e.yield_pct_per_week}%` : "—"}
                  </td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-tertiary)" }}>{e.contracts ?? "—"}</td>
                  <td className={td} style={{ color: /volatil|bear/i.test(e.regime || "") ? "#F59E0B" : "var(--hoip-text-tertiary)" }}>
                    {e.regime || "—"}
                  </td>
                  <td className={`${td} font-mono-data`} style={{ color: "var(--hoip-text-tertiary)" }}>
                    {e.iv_rank != null ? `${e.iv_estimated ? "~" : ""}${e.iv_rank}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function PositionManagementPage() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [riskOpen, setRiskOpen] = React.useState(false);
  const [mptOpen, setMptOpen] = React.useState(false);

  const load = React.useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await api.get(`/position-management/overview${force ? "?force=true" : ""}`);
      setData(d);
    } catch (e) {
      setError("Failed to load position overview.");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSelect = async (ticker, value) => {
    // optimistic update
    setData(prev => prev && ({
      ...prev,
      stocks: prev.stocks.map(s => s.ticker === ticker
        ? { ...s, assessment: { ...s.assessment, user_selected: value, agrees: value ? value === s.assessment?.suggested : null } }
        : s),
    }));
    try {
      await api.put("/position-management/assessment", { ticker, user_selected: value });
    } catch (e) { /* next reload restores truth */ }
  };

  const rh = data?.risk_health;
  const rhColor = rh?.status === "red" ? "#EF4444" : rh?.status === "amber" ? "#F59E0B" : "#10B981";

  return (
    <div className="p-4 space-y-3" data-testid="position-management-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>
            Position Management
          </h1>
          <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Per-position status against the rules you configured in Settings. Informational only — not trade instructions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMptOpen(true)} className="h-7 px-3 text-[10px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "rgba(139,92,246,0.4)", color: "#A78BFA" }}
            title="MPT / Monte Carlo efficient frontier — simulated projection, not a recommendation"
            data-testid="pm-mpt-open-btn">
            Portfolio Optimization
          </button>
          <button onClick={() => load(true)} className="h-7 px-3 text-[10px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            data-testid="pm-refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <PortfolioOptimizationModal open={mptOpen} onClose={() => setMptOpen(false)} />

      {rh && (
        <div className="border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
          data-testid="pm-risk-health-strip">
          <button onClick={() => setRiskOpen(!riskOpen)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left">
            <span className="w-2 h-2 rounded-full" style={{ background: rhColor }} />
            <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Portfolio Risk Health</span>
            <span className="text-[9px]" style={{ color: rh.breach_count ? rhColor : "var(--hoip-text-tertiary)" }}>
              {rh.breach_count ? `${rh.breach_count} limit${rh.breach_count !== 1 ? "s" : ""} flagged` : "all limits OK"}
            </span>
            <span className="ml-auto text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{riskOpen ? "▲" : "▼"}</span>
          </button>
          {riskOpen && <div className="px-3 pb-2"><RiskHealthPanel health={rh} compact /></div>}
        </div>
      )}

      {loading && (
        <div className="text-[11px] py-10 text-center" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pm-loading">
          Evaluating positions against your rules… <span className="block mt-1 text-[9px]">First load fetches live prices and 1-yr history — can take 20-45s.</span>
        </div>
      )}
      {error && <div className="text-[11px] py-4 text-center text-red-400" data-testid="pm-error">{error}</div>}

      {!loading && data && (
        <>
          <section className="border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
            data-testid="pm-stocks-section">
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
                Stock / Equity Positions <span className="font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>({data.stocks.length})</span>
              </span>
              <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                Sorted breach-first · hover a Rules tag for the triggered thresholds
              </span>
            </div>
            {data.stocks.length === 0 ? (
              <div className="px-3 py-6 text-center text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                No stock positions on file. Add them in Portfolio.
              </div>
            ) : <StocksTable stocks={data.stocks} onSelect={handleSelect} />}
          </section>

          <section className="border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
            data-testid="pm-income-section">
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="text-[11px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
                Options Income Positions <span className="font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>({data.income.length})</span>
              </span>
              <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                DTE · Δ · POT framework — same calculation as the Wheel Income roll rules
              </span>
            </div>
            {data.income.length === 0 ? (
              <div className="px-3 py-6 text-center text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                No open option legs in the Trade Log.
              </div>
            ) : data.income.map((r, i) => <IncomeRow key={`${r.ticker}-${r.trade_id || i}`} r={r} />)}
          </section>

          <CCCandidatesSection />

          <p className="text-[9px] leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }}>
            Each column — technical, regime, concentration, valuation, analyst consensus — is a separate, neutrally-labeled
            status check against rules you configured in Settings. The "Rules Status" is a computed starting point from
            a transparent rule matrix (hover the Rules tag to see exactly which thresholds were crossed); it is informational
            only, not personalized investment advice or a trade instruction — the decision you record is your own.
          </p>
        </>
      )}
    </div>
  );
}
